import { captchaAxios, masterAxios } from 'api/config'
import { Chat } from 'types/chat'
import { store } from 'store/index'
import { messagesActions, messagesSelectors, selectChatId } from 'store/messages'
import { ChatMessage } from 'types/chat-message'
import axios from 'axios'
import { settingsActions, settingsSelectors } from 'store/settings'
import { isEmpty, map } from 'lodash'
import { UploadService } from '@runelab/odin-common'
import { FirebaseAnalytics } from 'services/firebase'

type OpenNewChatParams = {
  areaId: string
}

const firebaseAnalytics = new FirebaseAnalytics()

export const openNewChat = async (params: OpenNewChatParams) => {
  const state = store.getState()
  const isLogged = settingsSelectors.isLogged(state)

  if (isLogged) {
    return await openNewLoggedChat(params)
  } else {
    return await openNewGuestChat(params)
  }
}

export const openNewGuestChat = async ({ areaId }: OpenNewChatParams) => {
  const { dispatch } = store
  const state = store.getState()
  const lang = settingsSelectors.lang(state)
  try {
    const { data: guestChat } = await captchaAxios.post<Chat>('chats/guest', { culture: lang, areaId })
    console.debug({ guestChat })
    dispatch(messagesActions.addChat(guestChat))
    dispatch(messagesActions.setChatId(guestChat._id))
    firebaseAnalytics.trackOpenChatArea(areaId, { user: guestChat.user, type: 'guest' })
  } catch (e) {
    console.error({ e })
    return Promise.reject(e)
  }
}

export const openNewLoggedChat = async ({ areaId }: OpenNewChatParams) => {
  const state = store.getState()
  const lang = settingsSelectors.lang(state)
  try {
    const { data: userChat } = await axios.post<Chat>('chats/user', {
      culture: lang,
      areaId,
    })
    store.dispatch(messagesActions.addChat(userChat))
    store.dispatch(messagesActions.setChatId(userChat._id))
    firebaseAnalytics.trackOpenChatArea(areaId, { user: userChat.user, type: 'logged' })
  } catch (e) {
    console.error({ e })
    return Promise.reject(e)
  }
}

export const getChatMessages = async (chatId: string) => {
  try {
    const { data } = await masterAxios.get<ChatMessage[]>('chat-messages', {
      params: {
        chatId,
        sort: 'createdAt',
        limit: 100,
      },
    })
    console.debug({ data })
    store.dispatch(messagesActions.setMessages(data))
    return data
  } catch (e) {
    console.error({ e })
    store.dispatch(messagesActions.setMessages([]))
    return []
  }
}

export const getMyChats = async ({ areaId }: { areaId?: string }) => {
  try {
    const { data } = await axios.get<Chat[]>('chats', { params: { areaId } })
    console.log('newChats', data)
    console.debug({ data })
    store.dispatch(messagesActions.addChats(data))

    const chatId = selectChatId(store.getState())

    const chatIndex = data.findIndex((chat) => chat._id === chatId)

    if (chatIndex === -1) {
      store.dispatch(messagesActions.setChatId(undefined))
      store.dispatch(messagesActions.setBotTyping(false))
      store.dispatch(messagesActions.setUserTyping(true))
      store.dispatch(messagesActions.setUserTypingPlaceholder('typeMessage'))
      store.dispatch(settingsActions.setTicketReminderVisible(false))
    }
  } catch (e) {
    console.error({ e })
  }
}

type MessageToOperatorParams = {
  message: string
  files: File[]
}

const createUploads = async (files: File[]) => {
  const service = new UploadService(axios)
  return await Promise.all(
    files.map((file) => {
      return service.uploadFile({
        file,
        uploadFileParam: {
          additionalInfo: {},
          linkedEntityId: '',
          linkedEntityType: '',
        },
      })
    }),
  )
}

export const sentMessageToOperator = async ({ message, files }: MessageToOperatorParams) => {
  const state = store.getState()
  const chatId = selectChatId(state)
  try {
    const uploads = await createUploads(files)
    console.debug({ uploads })
    const { data } = await axios.post(`chat-messages`, {
      message,
      chatId,
      attachmentIds: map(uploads, '_id'),
    })
    console.debug({ newMessage: data })
    store.dispatch(messagesActions.addMessage(data))
  } catch (e) {
    console.error({ e })
  }
}

export const assignGuestChatsToCurrentUser = async () => {
  const state = store.getState()
  const isLogged = settingsSelectors.isLogged(state)
  const guestChats = messagesSelectors.guestChats(state)

  const chatIds = map(guestChats, '_id')
  console.debug({ guestChats })

  if (isLogged) {
    if (isEmpty(chatIds)) {
      return Promise.resolve()
    }
    await axios.put('chats/assign/me', {
      chatIds: map(guestChats, '_id'),
    })
    await getMyChats({})
  } else {
    return Promise.resolve()
  }
}

export const markChatAsRead = async (chatId: string) => {
  try {
    const { data } = await axios.patch<Chat>(`chats/${chatId}/read`)
    store.dispatch(messagesActions.editChat(data))
  } catch (e) {
    console.error({ e })
  }
}

export const exportChat = async (chatId: string, email: string) => {
  try {
    const { data } = await axios.get(`chats/${chatId}/export/${email}`)
    console.debug({ exportChat: data })
  } catch (e) {
    console.error({ e })
  }
}
